import { useMemo } from 'react'
import type { RowData } from '@tanstack/react-table'

import DebouncedInput from '@/components/common/DebouncedInput'
import { className3 } from '@/components/common/styles'
import type { TableWrapperColumn, TableWrapperTable } from '@/lib/table/table'

function Filter<TData extends RowData>({
  column,
  table,
}: Readonly<{
  column: TableWrapperColumn<TData>
  table: TableWrapperTable<TData>
}>) {
  const firstValue = table
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id)
  const columnFilterValue = column.getFilterValue()

  const uniqueValues = column.getFacetedUniqueValues().keys()
  const sortedUniqueValues = useMemo(
    () =>
      (typeof firstValue === 'number'
        ? []
        : Array.from(uniqueValues).sort()) as string[],
    [firstValue, uniqueValues],
  )

  const columnType = typeof firstValue
  if (columnType === 'undefined') {
    return <></> // @@TODO: Return null instead of <></> ?
  } else if (columnType === 'number') {
    const min = Number(column.getFacetedMinMaxValues()?.[0] ?? '')
    const max = Number(column.getFacetedMinMaxValues()?.[1] ?? '')

    const currentFilter = columnFilterValue as [number, number]
    let maxForMin = max
    if (typeof max === 'number' && typeof currentFilter?.[1] === 'number') {
      maxForMin = Math.min(max, currentFilter?.[1])
    }
    let minForMax = min
    if (typeof min === 'number' && typeof currentFilter?.[0] === 'number') {
      minForMax = Math.max(min, currentFilter?.[0])
    }
    return (
      <div>
        <div>
          <DebouncedInput
            type="number"
            min={min}
            max={maxForMin}
            value={currentFilter?.[0] ?? ''}
            onChange={(value) => {
              // Only change min, keep max
              column.setFilterValue((old: [number, number]) => {
                return [value, old?.[1]]
              })
            }}
            placeholder={`Min ${min}`}
            className={`${className3} w-24 border shadow-sm rounded-sm`}
            aria-label="minimum value"
          />
        </div>
        <div>
          <DebouncedInput
            type="number"
            min={minForMax}
            max={max}
            value={currentFilter?.[1] ?? ''}
            onChange={(value) => {
              // Only change max, keep min
              column.setFilterValue((old: [number, number]) => {
                return [old?.[0], value]
              })
            }}
            placeholder={`Max ${max}`}
            className={`${className3} w-24 border shadow-sm rounded-sm`}
            aria-label="maximum value"
          />
        </div>
      </div>
    )
  } else {
    return (
      <div className="content-start items-start">
        <datalist id={column.id + 'list'}>
          {sortedUniqueValues.slice(0, 5000).map(
            (
              value: string | number, // @@TODO: Check types
            ) => (
              <option value={value} key={value} />
            ),
          )}
        </datalist>
        <DebouncedInput
          type="text"
          value={(columnFilterValue ?? '') as string}
          onChange={(value) => column.setFilterValue(value)}
          placeholder={`Search... (${column.getFacetedUniqueValues().size})`}
          className={`${className3} w-36 border shadow-sm rounded-sm`}
          list={column.id + 'list'}
          aria-label="search value"
        />
      </div>
    )
  }
}
export default Filter
